// 더 큰 요금제 출시 알림 신청 — 필요한 것(여러 개)·하고 싶은 말·받는 분/이메일(가입 정보로 채움) · 보내면 받음 화면.

import { FormEvent, useState, useTransition } from "react";

import { nameInputMax } from "@buildpy/core/modules/onboarding/rules";
import { PLAN_INTEREST_MEMO_MAX, PLAN_INTEREST_NAME_MAX, PLAN_INTEREST_NEED_LABEL, PLAN_INTEREST_NEEDS, type PlanInterestNeed } from "@buildpy/core/modules/onboarding/types";
import { Button } from "@buildpy/ui/button";
import { Input } from "@buildpy/ui/input";
import { Label } from "@buildpy/ui/label";
import SectionCard from "@buildpy/ui/section-card";
import { Textarea } from "@buildpy/ui/textarea";
import { submitPlanInterest } from "@/api/actions/onboarding";
import { Link } from "@/lib/nav";
import { PLAN_SETTINGS_PATH } from "@/modules/plan/copy";
import { NEEDS_REQUIRED_MESSAGE, PLAN_INTEREST_FAILED_MESSAGE } from "./plan-interest-form-state";

const BACK_LINK = "inline-flex min-h-11 items-center text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline";

interface Props {
  defaultName: string;
  defaultEmail: string;
}

export const PlanInterestSent = ({ message }: { message: string }) => (
  <SectionCard as="div" className="flex flex-col gap-y-3" data-testid="plan-interest-sent" padding="lg">
    <h1 className="text-xl font-bold">알림 신청을 받았습니다</h1>
    <p className="text-sm text-secondary-foreground">{message}</p>
    <Button asChild className="self-start" variant="outline">
      <Link href={PLAN_SETTINGS_PATH}>설정으로 돌아가기</Link>
    </Button>
  </SectionCard>
);

const PlanInterestForm = ({ defaultName, defaultEmail }: Props) => {
  const [needs, setNeeds] = useState<PlanInterestNeed[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  const [pending, startTransition] = useTransition();

  if (sent) return <PlanInterestSent message={sent} />;

  const toggle = (need: PlanInterestNeed) => setNeeds((prev) => (prev.includes(need) ? prev.filter((n) => n !== need) : [...prev, need]));

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (needs.length === 0) {
      setError(NEEDS_REQUIRED_MESSAGE);
      return;
    }
    const form = new FormData(event.currentTarget);
    const input = {
      needs,
      memo: String(form.get("memo") ?? "").trim() || undefined,
      name: String(form.get("name") ?? "").trim(),
      email: String(form.get("email") ?? "").trim(),
    };
    startTransition(async () => {
      try {
        const result = await submitPlanInterest(input);
        if (!result.ok) {
          setError(result.message);
          return;
        }
        setError(null);
        setSent(result.message);
      } catch {
        setError(PLAN_INTEREST_FAILED_MESSAGE);
      }
    });
  };

  return (
    <div className="flex flex-col gap-y-4">
      <Link className={BACK_LINK} href={PLAN_SETTINGS_PATH}>
        ← 설정 › 요금제
      </Link>
      <SectionCard as="form" className="flex flex-col gap-y-6" data-testid="plan-interest-form" noValidate onSubmit={onSubmit} padding="lg">
        <div className="flex flex-col gap-y-1">
          <h1 className="text-xl font-bold">더 큰 요금제 출시 알림</h1>
          <p className="text-sm text-secondary-foreground">
            Pro·Max 는 준비 중입니다. 필요한 걸 알려 주시면 요금제를 정할 때 반영하고, 나오면 먼저 연락드립니다. 지금 무료로 쓰시는 건 그대로입니다.
          </p>
        </div>

        <div className="flex flex-col gap-y-1.5">
          <span className="text-sm font-medium">무엇이 더 필요하신가요? · 여러 개 고를 수 있음</span>
          <div aria-label="필요한 것" className="flex flex-wrap gap-1.5" data-testid="plan-interest-needs" role="group">
            {PLAN_INTEREST_NEEDS.map((need) => (
              <Button
                key={need}
                aria-pressed={needs.includes(need)}
                data-value={need}
                disabled={pending}
                size="chip"
                type="button"
                variant="chip"
                onClick={() => toggle(need)}>
                {PLAN_INTEREST_NEED_LABEL[need]}
              </Button>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-y-1.5">
          <Label htmlFor="plan-interest-memo">하고 싶은 말 · 선택</Label>
          <Textarea disabled={pending} id="plan-interest-memo" maxLength={PLAN_INTEREST_MEMO_MAX} name="memo" rows={3} />
        </div>

        <fieldset className="flex flex-col gap-y-3">
          <legend className="mb-1.5 text-sm font-medium">어디로 알려 드릴까요?</legend>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-y-1.5">
              <Label htmlFor="plan-interest-name">받는 분</Label>
              <Input defaultValue={defaultName} disabled={pending} id="plan-interest-name" maxLength={nameInputMax(PLAN_INTEREST_NAME_MAX)} name="name" required />
            </div>
            <div className="flex flex-col gap-y-1.5">
              <Label htmlFor="plan-interest-email">이메일</Label>
              <Input defaultValue={defaultEmail} disabled={pending} id="plan-interest-email" inputMode="email" maxLength={254} name="email" required type="email" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">가입하신 정보로 채웠습니다. 출시 안내 외의 연락에는 쓰지 않습니다.</p>
        </fieldset>

        {error && (
          <p className="text-sm text-destructive" data-testid="plan-interest-error" role="alert">
            {error}
          </p>
        )}

        <div className="flex flex-wrap justify-end gap-2">
          <Button asChild variant="ghost">
            <Link href={PLAN_SETTINGS_PATH}>취소</Link>
          </Button>
          <Button data-testid="plan-interest-submit" disabled={pending} type="submit">
            {pending ? "보내는 중…" : "출시 알림 신청"}
          </Button>
        </div>
      </SectionCard>
    </div>
  );
};

export default PlanInterestForm;
